import type { AutocompletePlugin, AutocompleteSource } from '@algolia/autocomplete-core';
import { createQuerySuggestionsPlugin } from '@algolia/autocomplete-plugin-query-suggestions';
import { createLocalStorageRecentSearchesPlugin } from '@algolia/autocomplete-plugin-recent-searches';
import { createRedirectUrlPlugin } from '@algolia/autocomplete-plugin-redirect-url';
import { getAlgoliaResults } from '@algolia/autocomplete-preset-algolia';
import { type Dispatch, type SetStateAction, useCallback } from 'react';
import type { ProductHit } from '@libs/algolia';
import { AlgoliaIndice, createAlgoliaClient, getAlgoliaPersistentProductFilters, getProductUrl, isNumericSKU, SEARCH_PAGE_PATH } from '@libs/algolia';
import { setSearchStats, TrackingParams, trackLegacySearchEvent, trackSearchFlyoutClick, trackSubmitSearchEvent, useAnalyticsTags } from '@libs/analytics';
import { getENV, stripHtml, useDecisions } from '@libs/common-next';
import type { AutocompleteItem, SetAutocompleteStateOptions } from './types';
import { getSectionTitle } from './utils';
type SetAutocompleteState = Dispatch<SetStateAction<SetAutocompleteStateOptions>>;
type AutocompleteQuerySuggestionsHit = {
  query: string;
  __autocomplete_qsCategory?: string;
};
type RecentSearchesItem = {
  label: string;
  category: string;
};
type SuggestedCategoryWithQueryParamsProps = {
  query?: string;
  category?: string;
};
type AssistedUrlTrackingDataProps = {
  searchTerm: string;
  header: string;
  term: string;
};
const searchClient = createAlgoliaClient({
  caller: 'web:searchbar:autocomplete-client'
});
const redirectUrlPlugin = createRedirectUrlPlugin();
export const getSuggestedCategoryWithQuery = (query: string, category?: string): string | null => {
  if (category) {
    return `/${SEARCH_PAGE_PATH}?q=${stripHtml(query.trim())}&category=${encodeURIComponent(category)}&format=new`;
  }
  return `/${SEARCH_PAGE_PATH}?q=${stripHtml(query.trim())}&format=new`;
};
export const getAssistedUrl = (pluginId: string, term: string, item: RecentSearchesItem | AutocompleteQuerySuggestionsHit | AutocompleteItem) => {
  const suggestedCategoryWithQueryParams: SuggestedCategoryWithQueryParamsProps = {};
  const header = getSectionTitle(pluginId || '').toLowerCase();
  const autocompleteItem = item as AutocompleteItem;
  const searchTerm = autocompleteItem.name ?? autocompleteItem.label as string ?? autocompleteItem.objectID;
  const assistedParams = `st=assisted&sa=${header}&sterm=${term}`;
  switch (pluginId) {
    case 'productSuggestionsPlugin':
      {
        const isSkuId = isNumericSKU(term);
        const url = getProductUrl(item as ProductHit, isSkuId);
        setSearchStats({
          searchResults: 1,
          searchTerm: term,
          searchType: 'assisted',
          isSuggestedProduct: true
        });
        trackSearchFlyoutClick({
          term,
          header,
          url
        });
        trackSubmitSearchEvent(term, header, term);
        trackLegacySearchEvent(searchTerm, header);
        return {
          url
        };
      }
    case 'querySuggestionsPlugin':
      {
        const a = item as AutocompleteQuerySuggestionsHit;
        suggestedCategoryWithQueryParams.query = a.query;
        suggestedCategoryWithQueryParams.category = a.__autocomplete_qsCategory;
        setSearchStats({
          searchTerm: a.query,
          searchType: 'assisted'
        });
        break;
      }
    case 'recentSearchesPlugin':
      {
        const r = item as RecentSearchesItem;
        suggestedCategoryWithQueryParams.query = r.label;
        suggestedCategoryWithQueryParams.category = r.category;
        setSearchStats({
          searchTerm: r.label,
          searchType: 'assisted'
        });
        break;
      }
    default:
      {
        break;
      }
  }
  const categoryAndQuery = getSuggestedCategoryWithQuery(suggestedCategoryWithQueryParams.query || '', suggestedCategoryWithQueryParams.category);
  const urlParams = `${assistedParams}&sat=${term}`;
  const url = `${categoryAndQuery}&${urlParams}`;
  trackSearchFlyoutClick({
    term,
    header,
    url
  });
  return {
    url,
    trackingData: {
      searchTerm,
      header,
      term
    }
  };
};
const onSubmitSuggestion = (e: Event, url: string, trackingData: AssistedUrlTrackingDataProps) => {
  const elem = e.target as Element;
  const isSubmit = !(elem.classList.contains('autocomplete-assist-search') || elem.parentElement?.classList.contains('autocomplete-assist-search'));
  if (isSubmit) {
    const {
      searchTerm,
      header,
      term
    } = trackingData;
    trackSubmitSearchEvent(searchTerm, header, term);
    trackLegacySearchEvent(searchTerm, header);
    window.location.href = url;
  }
};
export const onSelectSuggestion = (setAutocompleteQuery: Dispatch<SetStateAction<string>>, pluginId: string) => ({
  item,
  event
}: {
  item: AutocompleteQuerySuggestionsHit;
  event: Event;
}) => {
  const {
    query
  } = item;
  setAutocompleteQuery(prevQuery => {
    const {
      url,
      trackingData
    } = getAssistedUrl(pluginId, prevQuery, item as AutocompleteQuerySuggestionsHit);
    onSubmitSuggestion(event, url, trackingData as AssistedUrlTrackingDataProps);
    return query;
  });
};
const debouncePromise = <F extends (...args: Parameters<F>) => ReturnType<F>,>(fn: F, time: number) => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  return function debounced(...args: Parameters<F>) {
    if (timer) {
      clearTimeout(timer);
    }
    if (!time) {
      return fn(...args);
    }
    return new Promise<ReturnType<F>>(resolve => {
      timer = setTimeout(() => resolve(fn(...args)), time);
    });
  };
};
export const useAlgoliaPlugins = (setAutocompleteState: SetAutocompleteState, setAutocompleteQuery: Dispatch<SetStateAction<string>>, productsHitPerPage: number) => {
  const {
    ff_ac_click_anx: enableClickAnalytics,
    ff_srch_deb: searchDebounceMs
  } = useDecisions();
  const searchAnalyticsTags = useAnalyticsTags(['autocomplete']);
  const debounced = useCallback(debouncePromise((items: AutocompleteSource<any>[]) => items, searchDebounceMs), [searchDebounceMs]);
  const recentSearches = getENV('NEXT_PUBLIC_ENABLE_RECENT_SEARCHES') ? createLocalStorageRecentSearchesPlugin({
    key: 'instantsearch',
    limit: 3,
    transformSource({
      source
    }) {
      const pluginId = 'recentSearchesPlugin';
      return {
        ...source,
        sourceId: pluginId,
        getItems(params) {
          return source.getItems(params);
        },
        onSelect({
          item,
          event
        }) {
          const {
            label
          } = item;
          setAutocompleteQuery(prevQuery => {
            const {
              url,
              trackingData
            } = getAssistedUrl(pluginId, prevQuery, item as RecentSearchesItem);
            onSubmitSuggestion(event, url, trackingData as AssistedUrlTrackingDataProps);
            return label;
          });
        }
      };
    }
  }) : {};
  const querySuggestions = createQuerySuggestionsPlugin({
    searchClient,
    indexName: AlgoliaIndice.PRODUCTS_QUERY_SUGGESTIONS,
    getSearchParams() {
      return {
        hitsPerPage: 6
      };
    },
    categoryAttribute: [AlgoliaIndice.PRODUCTS, 'facets', 'exact_matches', 'primary_category_name'],
    transformSource({
      source: source_0
    }) {
      const pluginId_0 = 'querySuggestionsPlugin';
      return {
        ...source_0,
        sourceId: pluginId_0,
        onSelect: onSelectSuggestion(setAutocompleteQuery, pluginId_0)
      };
    }
  });
  const productSuggestions: AutocompletePlugin<any, any> = {
    getSources() {
      const pluginId_1 = 'productSuggestionsPlugin';
      return debounced([{
        sourceId: pluginId_1,
        onSelect({
          item: item_0
        }: {
          item: AutocompleteItem;
        }) {
          if (item_0.id) {
            setAutocompleteQuery(prevQuery_0 => {
              const {
                url: url_0
              } = getAssistedUrl(pluginId_1, prevQuery_0, item_0);
              window.sessionStorage.setItem('pdpFindingMethod', TrackingParams.SEARCH_REFERER);
              window.location.href = url_0;
              return prevQuery_0;
            });
          }
        },
        getItems({
          query
        }: {
          query: string;
        }) {
          if (!query) {
            return [];
          }
          const persistentFilters = getAlgoliaPersistentProductFilters();
          return getAlgoliaResults({
            searchClient,
            queries: [{
              indexName: AlgoliaIndice.PRODUCTS,
              query,
              params: {
                hitsPerPage: productsHitPerPage,
                filters: persistentFilters,
                facetingAfterDistinct: true,
                clickAnalytics: enableClickAnalytics,
                analyticsTags: searchAnalyticsTags
              }
            }]
          });
        }
      }]);
    }
  };
  return [recentSearches, querySuggestions, productSuggestions, redirectUrlPlugin];
};